import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { useToast } from '@/components/common/toast';
import { useNotifications } from './useNotifications';
import { useNotificationEffect } from './notificationBus';
import { NotificationCategory, type NotificationDto } from '@/api/types/notifications';
import { formatDateOnly } from '@/utils/format';
import './NotificationBell.css';

const SYMBOL_AUTO_ADDED = NotificationCategory.SymbolAutoAdded;

function formatRelative(iso: string): string {
  if (!iso) return '';
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return '';
  const ms = Date.now() - dt.getTime();
  const mins = Math.floor(ms / 60000);
  const hrs = Math.floor(ms / 3600000);
  const days = Math.floor(ms / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  return formatDateOnly(iso);
}

function iconFor(n: NotificationDto): string {
  const t = `${n.title} ${n.message}`.toLowerCase();
  if (n.category === NotificationCategory.DealAlert || /deal|alert|trade/.test(t)) {
    return 'ri-line-chart-line';
  }
  return 'ri-notification-line';
}

interface DealData {
  login?: number | string;
  dataSourceId?: number | string;
  dealId?: number | string;
}

function parseDealData(n: NotificationDto): DealData | null {
  if (n.category !== NotificationCategory.DealAlert || !n.data) return null;
  try {
    return JSON.parse(n.data) as DealData;
  } catch {
    return null;
  }
}

export function NotificationBell({
  onOpenLogin,
}: {
  onOpenLogin: (login: number, connector: number) => void;
}) {
  const { grantedPolicies } = useAuth();
  const canSurveil = grantedPolicies['Needs.SurveillanceGateway'] === true;
  const toast = useToast();

  // Surface a toast for every live push so the user is told the moment something
  // finishes (e.g. background processing), not only when they open the bell.
  useNotificationEffect((n) => {
    const text = n.popupMessage || n.message || n.title;
    if (!text) return;
    if (/fail|error/i.test(`${n.title} ${n.message}`)) toast.error(text);
    else toast.info(text);
  });

  const [open, setOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [kebabOpen, setKebabOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const { items, unreadCount, loading, loadMore, markRead, markAllRead } =
    useNotifications(true);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setKebabOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function onScroll() {
    const el = listRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) loadMore();
  }

  function handleRowClick(n: NotificationDto) {
    if (!n.isRead) void markRead(n.id).catch(() => {});
    // DealAlert drill-down: surface the login overview (React analog of the legacy
    // AI-summary drill-down, which is gated on Needs.SurveillanceGateway).
    const deal = parseDealData(n);
    if (canSurveil && deal?.login != null) {
      const login = Number(deal.login);
      const connector = Number(deal.dataSourceId ?? 0);
      if (!isNaN(login)) {
        onOpenLogin(login, connector);
        setOpen(false);
      }
    }
  }

  const visible = unreadOnly ? items.filter((n) => !n.isRead) : items;

  return (
    <div
      className="header-element dropdown"
      ref={ref}
      style={{ position: 'relative' }}
      id="notificationBellDropdown"
    >
      <a
        href="#"
        id="notificationBell"
        className="header-link position-relative d-flex align-items-center"
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
      >
        <i className="ri-notification-3-line header-link-icon fs-18" />
        {unreadCount > 0 && (
          <span
            id="notificationCount"
            className="badge bg-primary rounded-pill header-icon-badge"
          >
            {unreadCount}
          </span>
        )}
      </a>

      <div
        className={`dropdown-menu dropdown-menu-end dropdown-menu-arrow shadow p-0 notification-dropdown ${open ? 'show' : ''}`}
        style={{
          position: 'absolute',
          right: 0,
          left: 'auto',
          top: '100%',
          marginTop: '0.25rem',
          minWidth: 340,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 d-flex justify-content-between align-items-center border-bottom">
          <h5 className="mb-0 fw-bold">Notifications</h5>
          <div className="d-flex align-items-center gap-2">
            <div className="form-check form-switch mb-0 ps-0 d-flex align-items-center">
              <label className="form-check-label me-2 small" htmlFor="toggleUnread">
                Show unread only
              </label>
              <input
                id="toggleUnread"
                className="form-check-input mt-0 ms-0"
                type="checkbox"
                role="switch"
                checked={unreadOnly}
                onChange={(e) => setUnreadOnly(e.target.checked)}
              />
            </div>

            <div className="dropdown" style={{ position: 'relative' }}>
              <button
                type="button"
                className="btn btn-sm bg-transparent border-0 p-1"
                title="More"
                onClick={() => setKebabOpen((v) => !v)}
              >
                <i className="ri-more-2-fill" />
              </button>
              <div
                className={`dropdown-menu dropdown-menu-end ${kebabOpen ? 'show' : ''}`}
                style={{ position: 'absolute', right: 0, left: 'auto' }}
              >
                <button
                  id="markAllAsRead"
                  type="button"
                  className="dropdown-item"
                  disabled={unreadCount === 0}
                  onClick={() => {
                    setKebabOpen(false);
                    void markAllRead().catch(() => {});
                  }}
                >
                  <i className="ri-check-double-line me-2" />
                  Mark all as read
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          id="notificationList"
          className="list-group overflow-auto"
          ref={listRef}
          onScroll={onScroll}
          style={{ maxHeight: 420 }}
        >
          {visible.length === 0 && !loading && (
            <div className="p-3 text-center text-muted">No data to show</div>
          )}

          {visible.map((n) => {
            const isAutoAdded = n.category === SYMBOL_AUTO_ADDED;
            return (
              <button
                key={n.id}
                type="button"
                className={`list-group-item list-group-item-action text-start ${n.isRead ? '' : 'bg-light'}`}
                data-id={n.id}
                onClick={() => handleRowClick(n)}
              >
                <div className="d-flex align-items-start">
                  <div className="flex-shrink-0 me-3 mt-1">
                    <i className={`${iconFor(n)} text-primary ri-lg`} />
                  </div>
                  <div className="flex-grow-1">
                    <div className="mb-1">{n.message || n.title}</div>
                    <small className="text-muted notification-time">
                      {formatRelative(n.creationTime)}
                    </small>
                    {isAutoAdded && (
                      <div>
                        <small className="text-primary fw-semibold">Review &amp; edit</small>
                      </div>
                    )}
                  </div>
                  {!n.isRead && (
                    <span
                      className="bg-primary rounded-circle ms-3 d-inline-block align-self-center"
                      style={{ width: 8, height: 8 }}
                    />
                  )}
                </div>
              </button>
            );
          })}

          {loading && (
            <div id="loading" className="text-center py-2">
              <span className="spinner-border spinner-border-sm" role="status" aria-label="Loading" />
              <span className="ms-1">Loading…</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
